import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import { maintenanceStatus } from "../domain/preservation.js";

/* What's under the bell.

   Two kinds of thing arrive from two completely different places, and keeping
   them apart is the whole design:

   - Care reminders are DERIVED, never stored. maintenanceStatus() already
     computes "this pair is due for cleaning" from the item's own dates for the
     Preservation Profile, so recomputing it here costs nothing and — the part
     that matters — cannot go stale. A stored reminder would survive the
     cleaning that resolved it and then have to be hunted down and deleted.
     This one stops existing the moment the item is actually cleaned.

   - Social notifications are FETCHED, because they are facts about what other
     people did and the browser has no way to know them. They are written
     server-side by triggers (see 0006); this app can read them, mark them
     read, and dismiss them, and has no privilege to create one. */

// Enough to fill the panel without paging. Beyond this, older ones are history.
const PAGE = 30;

/* The actor's name comes from profiles rather than being baked into the row at
   write time, so a collector who changes their display name isn't remembered
   under the old one. */
const SELECT = "id, type, actor_id, data, read, created_at, actor:profiles!notifications_actor_id_fkey(display_name)";

/** The table isn't there yet — this project is deployed ahead of its migration. */
function isMissingTable(err) {
  return err?.code === "42P01" || err?.code === "PGRST205";
}

export function useNotifications(items) {
  const { userId } = useAuth();
  const [social, setSocial] = useState([]);
  /* Distinct from "there are none". Until 0006 is applied the social half
     cannot work at all, and the bell should say nothing rather than claim an
     empty inbox — so the panel hides the section entirely. */
  const [socialAvailable, setSocialAvailable] = useState(true);

  const fetchOne = useCallback(async (id) => {
    const { data } = await supabase.from("notifications").select(SELECT).eq("id", id).maybeSingle();
    if (!data) return;
    setSocial((prev) => (prev.some((n) => n.id === data.id) ? prev : [data, ...prev].slice(0, PAGE)));
  }, []);

  useEffect(() => {
    if (!userId) {
      setSocial([]);
      return;
    }
    let cancelled = false;

    (async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select(SELECT)
        .order("created_at", { ascending: false })
        .limit(PAGE);

      if (cancelled) return;
      if (error) {
        /* Deliberately quiet. Nothing under the bell is load-bearing: the app
           is completely usable with an empty one, so a failure here must never
           produce an error banner over someone's collection. */
        if (!isMissingTable(error)) console.error("[notifications]", error);
        setSocialAvailable(!isMissingTable(error));
        setSocial([]);
        return;
      }
      // RLS already limits this to your own rows, so there is no user_id filter
      // above — the policy is the filter.
      setSocial(data || []);
      setSocialAvailable(true);
    })();

    /* A notification that needs a refresh to appear is barely a notification,
       so this listens for its own. The payload carries the raw row with no
       actor name attached, so the row is re-read by id — one primary-key
       lookup — rather than rendering "Someone" until the next load.

       Only INSERTs are watched. The trigger bumps an existing unread marker
       instead of inserting a second one for a busy group, and a bump changes
       nothing the panel displays. */
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${userId}` },
        (payload) => { if (!cancelled) fetchOne(payload.new.id); }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [userId, fetchOne]);

  const careReminders = useMemo(
    () =>
      (items || [])
        .map((item) => {
          const ms = maintenanceStatus(item);
          const name = [item.brand, item.model].filter(Boolean).join(" ") || "An item";
          if (ms.cleanDue) {
            return { id: `care_clean_${item.id}`, kind: "clean", itemId: item.id, label: `${name} is due for cleaning` };
          }
          if (ms.rotateDue) {
            return { id: `care_rotate_${item.id}`, kind: "rotate", itemId: item.id, label: `${name} is due for rotation` };
          }
          return null;
        })
        .filter(Boolean),
    [items]
  );

  /* Optimistic, and not rolled back on failure — unlike a follow or a save,
     the worst case is a dot that comes back on the next load, and re-marking
     it is one tap. Blocking the panel on a round-trip would be worse. */
  const markRead = useCallback(async (id) => {
    setSocial((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    // 0006 grants update on the `read` column alone, so this is the only field
    // that can be written from here even if this call said otherwise.
    const { error } = await supabase.from("notifications").update({ read: true }).eq("id", id);
    if (error) console.error("[notifications] markRead", error);
  }, []);

  const markAllRead = useCallback(async () => {
    const unread = social.filter((n) => !n.read).map((n) => n.id);
    if (!unread.length) return;
    setSocial((prev) => prev.map((n) => ({ ...n, read: true })));
    const { error } = await supabase.from("notifications").update({ read: true }).in("id", unread);
    if (error) console.error("[notifications] markAllRead", error);
  }, [social]);

  /* Care reminders are counted as unread because they stay true until the
     collector does the thing — there is no "read" state that would make sense
     for them. The count drops when the item is cleaned, which is the only
     event that should clear it. */
  const unreadCount = social.filter((n) => !n.read).length + careReminders.length;

  return { social, socialAvailable, careReminders, unreadCount, markRead, markAllRead };
}
